import { Monitor, Moon, Sun } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useThemeMode, type ThemeMode } from '@/lib/theme';

const OPTIONS: Array<{ mode: ThemeMode; label: string; icon: typeof Sun }> = [
  { mode: 'light', label: 'Светлая тема', icon: Sun },
  { mode: 'system', label: 'Как в системе', icon: Monitor },
  { mode: 'dark', label: 'Тёмная тема', icon: Moon },
];

/**
 * A three-way segmented control rather than a binary switch, because "follow
 * the OS" is a real preference for someone who works into the night.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const [mode, setMode] = useThemeMode();

  return (
    <div
      role="radiogroup"
      aria-label="Тема оформления"
      className={cn('inline-flex rounded-md border border-line bg-surface p-0.5', className)}
    >
      {OPTIONS.map(({ mode: option, label, icon: Icon }) => {
        const isActive = mode === option;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={label}
            title={label}
            onClick={() => setMode(option)}
            className={cn(
              // Three targets in a row, so each one needs a real 44px box —
              // an expanded pseudo-element would overlap its neighbours and
              // they would steal each other's taps.
              'focus-ring flex h-11 w-11 items-center justify-center rounded-sm',
              'transition-colors duration-fast lg:h-8 lg:w-8',
              isActive ? 'bg-sunken text-fg' : 'text-fg-muted hover:text-fg',
            )}
          >
            <Icon size={16} strokeWidth={1.5} aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
